import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';

export default function Home() {
  const features = [
    {
      icon: 'Zap',
      title: 'Instant Sharing',
      desc: 'Just tap your badge to a compatible phone to share your info instantly. No app required.'
    },
    {
      icon: 'Edit3',
      title: 'Always Up-to-Date',
      desc: 'Change jobs? Update your profile anytime from the dashboard. Your badge updates instantly.'
    },
    {
      icon: 'Shield',
      title: 'Eco-Friendly',
      desc: 'Stop printing paper cards. One TapBadge lasts a lifetime and reduces paper waste.'
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-48 overflow-hidden bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-50 via-white to-white"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block py-1 px-3 rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold mb-6">
                The Future of Networking
              </span>
              <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight mb-6">
                Share your identity with a <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">simple tap.</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-lg">
                Create your digital profile, get a premium NFC card, and never buy paper business cards again. Network smarter, not harder.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/store">
                  <Button size="lg" className="w-full sm:w-auto shadow-lg shadow-indigo-200">Shop TapBadges</Button>
                </Link>
                <Link to="/login">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto">Create Free Profile</Button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 50 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.8, delay: 0.2 }} 
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-indigo-200 to-purple-200 rounded-[2.5rem] transform rotate-3 scale-105 opacity-50 blur-lg"></div>
              <img 
                src="https://s3.us-east-2.wasabisys.com/crc-cdn/TabBadge/LTP-Tapbadge.png" 
                alt="Tapping NFC Card on Phone" 
                className="relative z-10 rounded-[2.5rem] shadow-2xl border border-white/50 w-full h-auto object-cover" 
              />
              
              {/* Floating Element */}
              <motion.div 
                animate={{ y: [0, -10, 0] }} 
                transition={{ repeat: Infinity, duration: 4 }}
                className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-4 z-20"
              >
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                  <SafeIcon name="Check" className="text-xl" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Contact Saved!</p>
                  <p className="text-sm text-gray-500">Just now</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How it works</h2>
            <p className="text-gray-600 text-lg">Set up your profile in minutes and start sharing instantly.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {features.map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mb-6 text-indigo-600">
                  <SafeIcon name={feature.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
